import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Loader2, Pencil, Send, Trash2, Upload } from "lucide-react";
import { PageLoader } from "@/components/common/loader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  deleteStagedUpload,
  listStagedUploads,
  renameStagedUpload,
  sendUploads,
  stageUpload,
} from "@/features/portal/api";
import { portalHref } from "@/features/portal/portal-base";
import type { PortalDocument } from "@/features/portal/types";

function formatBytes(bytes: number): string {
  if (!bytes) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  return `${(bytes / 1024 ** i).toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** `/portal/documents/upload` — stage files, then send them to the firm. */
export function PortalUpload() {
  const [staged, setStaged] = useState<PortalDocument[] | null>(null);
  const [uploading, setUploading] = useState(0);
  const [dragOver, setDragOver] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<PortalDocument | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const refetch = useCallback(async () => {
    try {
      const list = await listStagedUploads();
      setStaged(list);
    } catch {
      setStaged((prev) => prev ?? []);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  const hasStaged = (staged?.length ?? 0) > 0;

  // Warn before unloading the tab while files are staged and unsent.
  useEffect(() => {
    if (!hasStaged || sent) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasStaged, sent]);

  async function handleFiles(files: FileList | File[]) {
    const list = Array.from(files);
    if (list.length === 0) return;
    setSent(false);
    for (const file of list) {
      setUploading((n) => n + 1);
      try {
        await stageUpload(file);
      } catch (err) {
        toast.error(`${file.name}: ${(err as { message?: string })?.message ?? "Upload failed."}`);
      } finally {
        setUploading((n) => n - 1);
      }
    }
    await refetch();
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) void handleFiles(e.target.files);
    e.target.value = "";
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files) void handleFiles(e.dataTransfer.files);
  }

  function startRename(doc: PortalDocument) {
    setEditingId(doc.id);
    setEditingName(doc.name);
  }

  async function commitRename(id: string) {
    const name = editingName.trim();
    setEditingId(null);
    if (!name) {
      toast.error("Name cannot be blank.");
      return;
    }
    const current = staged?.find((d) => d.id === id);
    if (current && current.name === name) return;
    try {
      await renameStagedUpload(id, name);
      await refetch();
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "Rename failed.");
    }
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    const id = deleteTarget.id;
    setDeleteTarget(null);
    try {
      await deleteStagedUpload(id);
      await refetch();
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "Delete failed.");
    }
  }

  async function handleSend() {
    setSending(true);
    try {
      await sendUploads();
      setSendOpen(false);
      setSent(true);
      toast.success("Files sent to your firm.");
      await refetch();
    } catch (err) {
      toast.error((err as { message?: string })?.message ?? "Could not send files.");
    } finally {
      setSending(false);
    }
  }

  if (staged === null) return <PageLoader />;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6 py-8">
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-foreground">Upload Documents</h1>
        <Button asChild variant="outline">
          <Link to={portalHref("/documents")}>View Documents</Link>
        </Button>
      </div>

      {sent && !hasStaged && (
        <div className="flex items-center gap-2 rounded-md border border-primary/30 bg-primary/10 p-4 text-sm text-foreground">
          <Check className="size-4 text-primary" />
          Sent! Your firm has received your files.
        </div>
      )}

      {/* Drop zone */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => fileInputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") fileInputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-10 text-center transition-colors ${
          dragOver ? "border-primary bg-primary/5" : "border-border bg-muted/30"
        }`}
      >
        <Upload className="size-6 text-muted-foreground" />
        <p className="text-sm text-foreground">
          Drag &amp; drop files here, or <span className="font-medium text-primary">browse</span>
        </p>
        {uploading > 0 && (
          <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Loader2 className="size-3 animate-spin" />
            Uploading {uploading} {uploading === 1 ? "file" : "files"}…
          </p>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={onInputChange}
        />
      </div>

      {/* Staging list */}
      {hasStaged && (
        <Card className="gap-0 overflow-hidden p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Size</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {staged.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell className="font-medium text-foreground">
                    {editingId === doc.id ? (
                      <Input
                        autoFocus
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onBlur={() => commitRename(doc.id)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitRename(doc.id);
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        className="h-8 max-w-xs"
                      />
                    ) : (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 text-left hover:underline"
                        onClick={() => startRename(doc)}
                      >
                        {doc.name}
                        <Pencil className="size-3 text-muted-foreground" />
                      </button>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatBytes(doc.sizeBytes)}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => setDeleteTarget(doc)}
                    >
                      <Trash2 className="size-4" />
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <div className="flex justify-end">
        <Button disabled={!hasStaged} onClick={() => setSendOpen(true)}>
          <Send className="size-4" />
          Send files to us
        </Button>
      </div>

      {/* Send confirmation dialog */}
      <Dialog open={sendOpen} onOpenChange={(open) => !sending && setSendOpen(open)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send files to your firm</DialogTitle>
            <DialogDescription>
              Send {staged.length} {staged.length === 1 ? "file" : "files"} to your firm? You won't
              be able to change them after sending.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={sending} onClick={() => setSendOpen(false)}>
              Cancel
            </Button>
            <Button disabled={sending} onClick={handleSend}>
              {sending && <Loader2 className="size-4 animate-spin" />}
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete file</AlertDialogTitle>
            <AlertDialogDescription>
              Remove &ldquo;{deleteTarget?.name}&rdquo; from your staged files? This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                void confirmDelete();
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
